import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { set } from "lodash";
import { DynamicPage } from "../../Menu/Settings/page-builder/objects/dynamicPage";
import { PageBuilderGlobal } from "../../Menu/Settings/page-builder/hooks/getGlobals";
import { PageBuilderTemplate } from "../../Menu/Settings/page-builder/hooks/getTemplates";
import { UserPage } from "../../Menu/Settings/page-builder/objects/userPage";
import { InputFieldProps } from "../../Menu/Settings/page-builder/blocks/inputField";

export const DEFAULT_LEFT_PANEL_WIDTH = 345;

type State = {
    headerHeight: number,
    openBlocksList: boolean,
    openLayers: boolean,
    openPageParameters: boolean,
    screen: 'DESKTOP' | 'TABLET' | 'PHONE',
    pages: DynamicPage[] | null,
    globals: PageBuilderGlobal[] | null,
    templates: PageBuilderTemplate[] | null,
    favorites: string[] | null,
    userPage: {
        isSuccess: boolean,
        data: UserPage | null
    },
    pageParameters: {
        name: string,
        url: string,
        parent: DynamicPage | null
    },
    leftColumnWidth: number | null,
    isDraggingLeftPanel: boolean,
    filterFieldOptions: {
        [id: string]: {
            label?: {
                [locale: string]: string
            },
            options?: InputFieldProps['options'],
            value?: string | null
        }
    }
}

const initialState: State = {
    headerHeight: 0,
    openBlocksList: true,
    openLayers: false,
    openPageParameters: false,
    screen: 'DESKTOP',
    pages: null,
    globals: null,
    templates: null,
    favorites: null,
    userPage: {
        isSuccess: false,
        data: null
    },
    pageParameters: {
        name: '',
        url: '',
        parent: null
    },
    leftColumnWidth: null,
    isDraggingLeftPanel: false,
    filterFieldOptions: {}
}

const slice = createSlice({
    name: 'pageBuilder',
    initialState,
    reducers: {
        addNewGlobal: (
            state,
            action: PayloadAction<{ global: PageBuilderGlobal }>
        ) => {
            state.globals = (state.globals ?? []).concat([action.payload.global]);
        },
        addNewPage: (
            state,
            action: PayloadAction<{ page: DynamicPage }>
        ) => {
            state.pages?.push(action.payload.page);
        },
        addNewTemplate: (
            state,
            action: PayloadAction<{ template: PageBuilderTemplate }>
        ) => {
            state.templates = (state.templates ?? []).concat([action.payload.template]);
        },
        deleteGlobal: (
            state,
            action: PayloadAction<number>
        ) => {
            state.globals = state.globals?.filter((item) => {
                return item.id !== action.payload;
            }) ?? null;
        },
        deletePage: (
            state,
            action: PayloadAction<number>
        ) => {
            state.pages = state.pages?.filter((item) => {
                return item.id !== action.payload;
            }) ?? null;
        },
        deleteTemplate: (
            state,
            action: PayloadAction<number>
        ) => {
            state.templates = state.templates?.filter((item) => {
                return item.id !== action.payload;
            }) ?? null;
        },
        closeBlocksList: (
            state
        ) => {
            state.openBlocksList = false;
        },
        closeLayers: (
            state
        ) => {
            state.openLayers = false;
        },
        closePageParameters: (
            state
        ) => {
            state.openPageParameters = false;
        },
        openBlocksList: (
            state
        ) => {
            state.openBlocksList = true;
        },
        openLayers: (
            state
        ) => {
            state.openLayers = true;
        },
        openPageParameters: (
            state
        ) => {
            state.openPageParameters = true;
        },
        replaceGlobal: (
            state,
            action: PayloadAction<{ global: PageBuilderGlobal }>
        ) => {
            state.globals = state.globals?.map((item) => {
                if (item.id === action.payload.global.id) {
                    return action.payload.global;
                }
                return item;
            }) ?? null;
        },
        replacePage: (
            state,
            action: PayloadAction<{ page: DynamicPage }>
        ) => {
            state.pages = state.pages?.map((item) => {
                if (item.id === action.payload.page.id) {
                    return action.payload.page;
                }
                return item;
            }) ?? null;
        },
        setFavorites: (
            state,
            action: PayloadAction<State['favorites']>
        ) => {
            state.favorites = action.payload
        },
        setGlobals: (
            state,
            action: PayloadAction<State['globals']>
        ) => {
            state.globals = action.payload
        },
        setHeaderHeight: (
            state,
            action: PayloadAction<State['headerHeight']>
        ) => {
            state.headerHeight = action.payload
        },
        setLeftColumnWidth: (
            state,
            action: PayloadAction<{ diff: number }>
        ) => {
            const newSize = (state.leftColumnWidth ?? 0) + action.payload.diff;

            if (newSize > window.screen.width / 2) {
                state.leftColumnWidth = window.screen.width / 2;
            } else if (newSize < 200) {
                state.leftColumnWidth = 200;
            } else {
                state.leftColumnWidth = newSize;
            }
        },
        setLeftColumnWidthRaw: (
            state,
            action: PayloadAction<State['leftColumnWidth']>
        ) => {
            state.leftColumnWidth = action.payload;
        },
        setPages: (
            state,
            action: PayloadAction<State['pages']>
        ) => {
            state.pages = action.payload
        },
        setPageParameter: <K extends keyof State['pageParameters']>(
            state: any,
            action: PayloadAction<{
                key: K,
                value: State['pageParameters'][K]
            }>
        ) => {
            state.pageParameters[action.payload.key] = action.payload.value;
        },
        setScreen: (
            state,
            action: PayloadAction<State['screen']>
        ) => {
            state.screen = action.payload
        },
        setUserPage: (
            state,
            action: PayloadAction<State['userPage']>
        ) => {
            state.userPage = action.payload
        },
        setTemplates: (
            state,
            action: PayloadAction<State['templates']>
        ) => {
            state.templates = action.payload
        },
        setIsDraggingLeftPanel: (
            state,
            action: PayloadAction<State['isDraggingLeftPanel']>
        ) => {
            state.isDraggingLeftPanel = action.payload
        },
        setFilterFieldOptionValue(
            state,
            value: PayloadAction<{ id: string, value: string | null }>
        ): void {
            set(
                state.filterFieldOptions,
                `${value.payload.id}.value`,
                value.payload.value
            );
        },
        setFilterFieldOptionOptions(
            state,
            value: PayloadAction<{ id: string, options: InputFieldProps['options'] }>
        ): void {
            set(
                state.filterFieldOptions,
                `${value.payload.id}.options`,
                value.payload.options
            );
        },
        setFilterFieldOptionLabel(
            state,
            value: PayloadAction<{ id: string, label: InputFieldProps['label'] }>
        ): void {
            set(
                state.filterFieldOptions,
                `${value.payload.id}.label`,
                value.payload.label
            );
        },
        toggleBlocksList: (
            state
        ) => {
            state.openBlocksList = !state.openBlocksList;
        },
    }
});

export default slice;

export const {
    addNewGlobal,
    addNewPage,
    addNewTemplate,
    deleteGlobal,
    deletePage,
    deleteTemplate,
    closeBlocksList,
    closeLayers,
    closePageParameters,
    openBlocksList,
    openLayers,
    openPageParameters,
    replaceGlobal,
    replacePage,
    setFavorites,
    setGlobals,
    setHeaderHeight,
    setLeftColumnWidth,
    setLeftColumnWidthRaw,
    setPages,
    setScreen,
    setUserPage,
    setTemplates,
    setIsDraggingLeftPanel,
    setFilterFieldOptionValue,
    setFilterFieldOptionOptions,
    setFilterFieldOptionLabel,
    toggleBlocksList
} = slice.actions;

export const setPageParameter = slice.actions.setPageParameter as <
    K extends keyof State['pageParameters'],
>(payload: {
    key: K,
    value: State['pageParameters'][K],
}) => PayloadAction<{
    key: K,
    value: State['pageParameters'][K],
}>;
